import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    CircularProgress,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SectionCard } from '../../markets/components/shared';
import { openInNewTab } from '../../../utils/newTab';
import { useContainerWidth } from '../../markets/hooks/useContainerWidth';
import { usePiprChart } from '../api/usePiprChart';
import { localDateTime, localZone, tsDate } from '../chart/format';
import { MonitorToolbar, toolbarToggleSx } from '../chart/MonitorToolbar';
import { useMonitorPalette } from '../chart/palette';
import { pickResolution, resolutionLabel } from '../chart/resolution';
import { useLiveDomain } from '../chart/useLiveDomain';
import { computeLayout, MIN_CHART_HEIGHT } from './layout';
import { eventPath } from './links';
import {
    buildFwdModal,
    buildMeetingDates,
    buildModel,
    buildPoints,
    type Hover,
    type ModeKey,
} from './model';
import { PiprChart } from './PiprChart';
import { PiprHeader } from './PiprHeader';
import { PiprMeetings } from './PiprMeetings';
import { PiprSummary } from './PiprSummary';
import {
    DEFAULT_RANGE,
    FALLBACK_HISTORY_START_MS,
    focusRange,
    isLiveRange,
    isRangeKey,
    minuteNow,
    parseAbsolute,
    QUICK_RANGES,
    rangeFor,
    type RangeContext,
    type RangeKey,
} from './ranges';
import { S } from './strings';
import { buildSummary, forwardMeetingIndex } from './summary';
import type { BankInfo } from './types';

const CHART_HEIGHT_SHARE = 0.55;
const FULLSCREEN_CHROME = 96;
const REFRESH_MS = 60_000;

interface BankMeta {
    bank: string;
    historyStartMs: number;
    meetingMs: number[];
    meetingDates: string[];
}

function useWindowHeight(): number {
    const [height, setHeight] = useState(() => window.innerHeight);
    useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return height;
}

function resolveRange(
    absolute: [Date, Date] | null,
    rangeKey: RangeKey | null,
    focusMs: number | null,
    ctx: RangeContext
): [Date, Date] {
    const fallback = rangeFor(DEFAULT_RANGE, ctx) ??
        rangeFor('all', ctx) ?? [
            new Date(ctx.nowMs - 86_400_000),
            new Date(ctx.nowMs),
        ];
    if (absolute) return absolute;
    if (rangeKey) return rangeFor(rangeKey, ctx) ?? fallback;
    if (focusMs !== null) return focusRange(focusMs, ctx) ?? fallback;
    return fallback;
}

export function PiprPage() {
    const theme = useTheme();
    const palette = useMonitorPalette();
    const navigate = useNavigate();
    const { bank: bankParam } = useParams<{ bank: string }>();
    const bank = (bankParam ?? 'fed').toLowerCase();
    const [params, setParams] = useSearchParams();

    const [nowMs, setNowMs] = useState(() => minuteNow());
    useEffect(() => {
        const id = setInterval(() => setNowMs(minuteNow()), REFRESH_MS);
        return () => clearInterval(id);
    }, []);

    const [meta, setMeta] = useState<BankMeta | null>(null);
    const [banks, setBanks] = useState<readonly BankInfo[]>([]);
    const bankMeta = meta?.bank === bank ? meta : null;

    const historyStartMs =
        bankMeta?.historyStartMs ?? FALLBACK_HISTORY_START_MS;
    const meetingMs = bankMeta?.meetingMs ?? null;
    const ctx = useMemo<RangeContext>(
        () => ({ nowMs, historyStartMs, meetingMs }),
        [nowMs, historyStartMs, meetingMs]
    );

    const fromParam = params.get('from');
    const toParam = params.get('to');
    const rangeParam = params.get('range');
    const meetingParam = params.get('meeting');

    const absolute = parseAbsolute(fromParam, toParam, ctx);
    const rangeKey: RangeKey | null = absolute
        ? null
        : isRangeKey(rangeParam)
          ? rangeParam
          : null;
    const highlight =
        meetingParam && bankMeta
            ? bankMeta.meetingDates.indexOf(meetingParam)
            : -1;
    const highlightIdx = highlight >= 0 ? highlight : null;
    const focusMs =
        highlightIdx !== null && bankMeta
            ? bankMeta.meetingMs[highlightIdx]
            : null;

    const resolved = resolveRange(absolute, rangeKey, focusMs, ctx);
    const startMs = resolved[0].getTime();
    const endMs = resolved[1].getTime();
    const committed = useMemo<[Date, Date]>(
        () => [new Date(startMs), new Date(endMs)],
        [startMs, endMs]
    );
    const bounds = useMemo<[Date, Date]>(
        () => [new Date(historyStartMs), new Date(nowMs)],
        [historyStartMs, nowMs]
    );
    const isLive = isLiveRange(committed, nowMs);
    const activeKey: RangeKey | null =
        rangeKey ??
        (!absolute && focusMs === null && !meetingParam ? DEFAULT_RANGE : null);

    const updateParams = useCallback(
        (update: (p: URLSearchParams) => void) =>
            setParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    update(next);
                    return next;
                },
                { replace: true }
            ),
        [setParams]
    );

    const commit = useCallback(
        (next: [Date, Date]) =>
            updateParams(p => {
                p.delete('range');
                p.set('from', next[0].toISOString());
                p.set('to', next[1].toISOString());
            }),
        [updateParams]
    );
    const live = useLiveDomain(committed, bounds, commit);

    const onQuickRange = useCallback(
        (id: string) =>
            updateParams(p => {
                p.delete('from');
                p.delete('to');
                p.set('range', id);
            }),
        [updateParams]
    );
    const onReset = useCallback(
        () =>
            updateParams(p => {
                p.delete('from');
                p.delete('to');
                p.delete('range');
            }),
        [updateParams]
    );

    const onBank = useCallback(
        (next: string) => {
            const p = new URLSearchParams(params);
            p.delete('meeting');
            const query = p.toString();
            navigate(`/monitor/pipr/${next}${query ? `?${query}` : ''}`);
        },
        [navigate, params]
    );

    const [fullscreen, setFullscreen] = useState(false);
    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const windowHeight = useWindowHeight();
    const chartHeight = fullscreen
        ? Math.max(MIN_CHART_HEIGHT, windowHeight - FULLSCREEN_CHROME)
        : Math.max(
              MIN_CHART_HEIGHT,
              Math.round(windowHeight * CHART_HEIGHT_SHARE)
          );

    const { ref, width } = useContainerWidth();
    const histW = width > 0 ? computeLayout(width, chartHeight).histW : 0;

    const [resolutionChoice, setResolutionChoice] = useState<number | null>(
        null
    );
    const resolution =
        histW > 0 ? pickResolution(committed, histW, resolutionChoice) : null;

    const request = useMemo(
        () =>
            resolution === null
                ? null
                : { bank, from: committed[0], to: committed[1], resolution },
        [bank, committed, resolution]
    );
    const chart = usePiprChart(request);
    const data = chart.data?.bank === bank ? chart.data : null;

    const metaRef = useRef(meta);
    metaRef.current = meta;
    useEffect(() => {
        if (!data) return;
        setBanks(data.banks);
        const next: BankMeta = {
            bank: data.bank,
            historyStartMs: data.historyStartTs * 1000,
            meetingMs: data.meetings.map(m => m.decisionTs * 1000),
            meetingDates: data.meetings.map(m => m.meetingDate),
        };
        const prev = metaRef.current;
        const same =
            prev !== null &&
            prev.bank === next.bank &&
            prev.historyStartMs === next.historyStartMs &&
            prev.meetingDates.join() === next.meetingDates.join();
        if (!same) setMeta(next);
    }, [data]);

    const points = useMemo(() => (data ? buildPoints(data) : []), [data]);
    const meetingDates = useMemo(
        () => (data ? buildMeetingDates(data) : []),
        [data]
    );
    const fwdModal = useMemo(() => (data ? buildFwdModal(data) : []), [data]);

    const [mode, setMode] = useState<ModeKey>('rate');
    const [hover, setHover] = useState<Hover>(null);
    useEffect(() => setHover(null), [data]);

    const model = useMemo(
        () =>
            data && width > 0
                ? buildModel({
                      data,
                      points,
                      meetingDates,
                      fwdModal,
                      width,
                      height: chartHeight,
                      domain: live.domain,
                      mode,
                      isLive,
                  })
                : null,
        [
            data,
            points,
            meetingDates,
            fwdModal,
            width,
            chartHeight,
            live.domain,
            mode,
            isLive,
        ]
    );

    const summary = useMemo(
        () => (data ? buildSummary(data, points, hover, S, palette.ink) : null),
        [data, points, hover, palette.ink]
    );

    const targetOf = useCallback(
        (h: Exclude<Hover, null>): string | null => {
            if (!data) return null;
            const deltas = data.history.deltas;
            if (h.kind === 'hist') {
                if (h.j === null) return null;
                const d = points[h.i];
                const meeting = d ? data.meetings[d.meetingIdx] : undefined;
                if (!meeting) return null;
                return eventPath(meeting, {
                    levelBp: deltas[h.j],
                    range: committed,
                });
            }
            const idx = forwardMeetingIndex(data, h.i);
            const meeting = idx !== null ? data.meetings[idx] : undefined;
            if (!meeting) return null;
            return eventPath(meeting, {
                levelBp: h.j !== null ? deltas[h.j] : null,
            });
        },
        [data, points, committed]
    );
    const canActivate = useCallback(
        (h: Exclude<Hover, null>) => targetOf(h) !== null,
        [targetOf]
    );
    // The chart's market is a different page: open it beside the chart so the
    // zoom, range and bank the user picked stay where they are.
    const onActivate = useCallback(
        (h: Exclude<Hover, null>) => {
            const to = targetOf(h);
            if (to) openInNewTab(to);
        },
        [targetOf]
    );

    const quickRanges = useMemo(
        () =>
            QUICK_RANGES.map(q => ({
                id: q.id,
                label: q.label,
                disabled: rangeFor(q.id, ctx) === null,
                active: activeKey === q.id,
            })),
        [ctx, activeKey]
    );

    const unknownBank = banks.length > 0 && !banks.some(b => b.bank === bank);
    const historyLabel =
        resolution !== null
            ? `${S.history} · ${resolutionLabel(resolution)}`
            : S.history;
    const errorText =
        chart.error === 'schema'
            ? S.errSchema
            : chart.error === 'empty'
              ? S.errEmpty
              : chart.error === 'network'
                ? S.errNetwork
                : null;

    return (
        <Box
            sx={{
                height: '100%',
                overflowY: 'auto',
                px: { xs: 1.5, md: 3 },
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                '& > *': { flexShrink: 0 },
            }}
        >
            <PiprHeader bank={bank} banks={banks} onBank={onBank} />

            {unknownBank && (
                <Alert severity="warning">
                    There is no PIPR for “{bank}”. Pick one of the central banks
                    above.
                </Alert>
            )}

            <SectionCard sx={{ p: 2 }}>
                {summary ? (
                    <PiprSummary
                        summary={summary}
                        pendingOpen={Boolean(
                            data && data.forward.pendingBp !== 0
                        )}
                        palette={palette}
                    />
                ) : (
                    <Skeleton variant="rounded" width="100%" height={150} />
                )}
            </SectionCard>

            <SectionCard
                sx={
                    fullscreen
                        ? {
                              position: 'fixed',
                              inset: 0,
                              zIndex: theme.zIndex.modal,
                              p: 2,
                              borderRadius: 0,
                              overflowY: 'auto',
                              bgcolor: 'background.default',
                          }
                        : { p: 1.5 }
                }
            >
                <MonitorToolbar
                    displayRange={live.domain}
                    zoomed={absolute !== null}
                    quickRanges={quickRanges}
                    onQuickRange={onQuickRange}
                    resolutionChoice={resolutionChoice}
                    resolutionLabel={
                        resolution !== null ? resolutionLabel(resolution) : ''
                    }
                    onResolutionChange={setResolutionChoice}
                    fullscreen={fullscreen}
                    onToggleFullscreen={() => setFullscreen(v => !v)}
                    controls={
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={mode}
                            onChange={(_, v: ModeKey | null) => {
                                if (v) setMode(v);
                            }}
                            sx={toolbarToggleSx}
                        >
                            <ToggleButton value="rate">{S.m_rate}</ToggleButton>
                            <ToggleButton value="delta">
                                {S.m_delta}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    }
                >
                    {chart.loading && data && (
                        <CircularProgress
                            size={14}
                            sx={{ color: 'text.secondary' }}
                        />
                    )}
                </MonitorToolbar>

                <Box
                    ref={ref}
                    sx={{
                        width: '100%',
                        position: 'relative',
                        minHeight: chartHeight,
                    }}
                >
                    {model && (
                        <PiprChart
                            model={model}
                            points={points}
                            palette={palette}
                            strings={S}
                            historyLabel={historyLabel}
                            highlightMeeting={highlightIdx}
                            hover={hover}
                            onHover={setHover}
                            onZoom={live.zoomAt}
                            onPan={live.panBy}
                            onReset={onReset}
                            onActivate={onActivate}
                            canActivate={canActivate}
                        />
                    )}
                    {!model && chart.loading && (
                        <Skeleton
                            variant="rounded"
                            width="100%"
                            height={chartHeight}
                        />
                    )}
                    {errorText && !model && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'grid',
                                placeItems: 'center',
                                px: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    color: 'text.secondary',
                                }}
                            >
                                {errorText}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        mt: 1,
                        minHeight: 18,
                    }}
                >
                    {data
                        ? `${S.asOf} ${localDateTime(tsDate(data.forward.asofTs))}  ·  ${S.times} ${localZone()}`
                        : ' '}
                    {errorText && model ? `  ·  ${errorText}` : ''}
                </Typography>
            </SectionCard>

            {data && (
                <PiprMeetings
                    data={data}
                    range={committed}
                    highlight={highlightIdx}
                    ink={palette.ink}
                />
            )}
        </Box>
    );
}
